import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../components/AuthContext";
import { joinFifaRoom } from "./fifaApi";
import "./FifaPage.css";

export const FIFA_PENDING_INVITE_KEY = "fifa_pending_invite";

function normalizeCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "")
    .slice(0, 24);
}

export default function FifaInvitePage() {
  const { leagueCode } = useParams();
  const navigate = useNavigate();
  const { user, isAuthReady } = useContext(AuthContext);
  const code = useMemo(() => normalizeCode(leagueCode), [leagueCode]);
  const [league, setLeague] = useState(null);
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);
  const joinStarted = useRef(false);

  useEffect(() => {
    if (!code) return;
    window.localStorage.setItem(FIFA_PENDING_INVITE_KEY, code);
    let alive = true;
    fetch(`/api/fifa-invites/${encodeURIComponent(code)}`, {
      credentials: "include",
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || "room_not_found");
        if (alive) setLeague(payload?.league || null);
      })
      .catch((loadError) => {
        if (alive) {
          setError(
            loadError?.message === "room_not_found"
              ? "Ta liga nie istnieje albo zaproszenie jest już nieaktywne."
              : "Nie udało się sprawdzić zaproszenia."
          );
        }
      });
    return () => {
      alive = false;
    };
  }, [code]);

  useEffect(() => {
    if (!isAuthReady || !user || !code || error || joinStarted.current) return;
    joinStarted.current = true;
    setJoining(true);
    joinFifaRoom(code)
      .then((payload) => {
        window.localStorage.removeItem(FIFA_PENDING_INVITE_KEY);
        navigate(`/fifa/${encodeURIComponent(code)}`, {
          replace: true,
          state: {
            fifaInviteNotice: payload.alreadyParticipant
              ? `Już należysz do ligi „${payload.room?.title || code}”.`
              : `Dołączono do ligi „${payload.room?.title || code}”.`,
          },
        });
      })
      .catch((joinError) => {
        joinStarted.current = false;
        setJoining(false);
        setError(
          joinError?.message === "room_not_found"
            ? "Ta liga nie istnieje albo zaproszenie jest już nieaktywne."
            : "Nie udało się dołączyć do ligi."
        );
      });
  }, [code, error, isAuthReady, navigate, user]);

  const authSuffix = `?fifaInvite=${encodeURIComponent(code)}`;

  return (
    <div className="fifa-shell">
      <div className="glass-container fifa-page fifa-invite-page">
        <div className="panel-card fifa-panel fifa-invite-card">
          <div>
            <span className="fifa-invite-kicker">Zaproszenie do ligi</span>
            <h1 className="fifa-title">{league?.title || "Liga typera FIFA 2026"}</h1>
            <p className="fifa-muted">
              Kod: <strong>{code}</strong>
              {league ? ` · ${league.participantsCount} graczy` : ""}
            </p>
          </div>

          {error ? <div className="fifa-inline-message fifa-inline-message--error">{error}</div> : null}
          {!error && joining ? (
            <div className="fifa-inline-message fifa-inline-message--info">
              Dołączamy Cię do ligi...
            </div>
          ) : null}
          {!error && isAuthReady && !user ? (
            <>
              <p className="fifa-muted">
                Zarejestruj się albo zaloguj, aby dołączyć. Po uwierzytelnieniu dodamy Cię do tej
                ligi automatycznie.
              </p>
              <div className="fifa-invite-actions">
                <Link className="btn btn-primary" to={`/register${authSuffix}`}>
                  Zarejestruj się
                </Link>
                <Link className="btn btn-secondary" to={`/login${authSuffix}`}>
                  Zaloguj się
                </Link>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
